import { Fragment, useState } from 'react';

import SemenSearch from '../../semen/forms/search';
import { IconPlus, IconTrash } from '../../../common/icons';
import { STAGES, PRESERVATION, embryosCreated, stagesSum } from '../../../core/constants/clivage';

// Grilla de clivage organizada POR DONADORA (flujo v1): cada donadora es una
// fila-encabezado (a todo el ancho, sin columna "Donante" → la tabla no desborda)
// con sus clivages apilados debajo (cascada). El "+" compacto va en línea con el
// ÚLTIMO clivage de la donadora (append a nivel de donadora); el encabezado tiene
// su propio botón para desplegar/colapsar. "Emb." = sin DG (0025 §2).

function Chevron({ open }) {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
        </svg>
    );
}

export default function ClivageRows({ rows, errors, sameSemen, globalSemen, onField, onStage, onSemen, onClearSemen, onSexed, onAddClivage, onRemove }) {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const toggle = (aid) => setCollapsed((s) => { const n = new Set(s); if (n.has(aid)) n.delete(aid); else n.add(aid); return n; });
    // El medidor de la donadora cuenta solo lo que se GUARDARÁ (semen efectivo o
    // embriones), igual que la validación → no marca "over" por filas sembradas sin fertilizar.
    const willPost = (r) => !(r.oocytes === '' && stagesSum(r.stages) === 0) && (!!(sameSemen ? globalSemen : r.semen) || stagesSum(r.stages) > 0);

    const groups = [];
    const idx = new Map();
    rows.forEach((r) => {
        if (!idx.has(r.aspiration_id)) { idx.set(r.aspiration_id, groups.length); groups.push({ aspiration_id: r.aspiration_id, items: [] }); }
        groups[idx.get(r.aspiration_id)].items.push(r);
    });
    const colSpan = (sameSemen ? 0 : 1) + STAGES.length + 5; // óv + stages + emb + preserv + sex + acciones

    const fields = (r) => {
        const e = errors[r.key] || {};
        return (
            <>
                {!sameSemen && (
                    <td className="cl-td-semen">
                        <SemenSearch value={r.semen} onSelect={(s) => onSemen(r.key, s)} onClear={() => onClearSemen(r.key)} />
                        {e.semen && <span className="cl-cell-error" role="alert">{e.semen}</span>}
                    </td>
                )}
                <td className="cl-td-num">
                    <input type="number" min="0" inputMode="numeric" className={`cl-num-input ${e.oocytes ? 'cl-input--bad' : ''}`}
                        title={e.oocytes || undefined} value={r.oocytes} onChange={(ev) => onField(r.key, 'oocytes', ev.target.value)} />
                </td>
                {STAGES.map((s) => (
                    <td key={s.value} className="cl-td-num">
                        <input type="number" min="0" inputMode="numeric" className="cl-num-input cl-stage-input" style={{ '--stage-color': s.color }}
                            value={r.stages?.[s.value] ?? ''} onChange={(ev) => onStage(r.key, s.value, ev.target.value)} placeholder="0" />
                    </td>
                ))}
                <td className="cl-td-num">
                    <input type="number" value={embryosCreated(r.stages)} readOnly tabIndex={-1} title={e.embryos || undefined} className={`cl-num-input cl-input--readonly ${e.embryos ? 'cl-input--bad' : ''}`} />
                </td>
                <td className="cl-td-pres">
                    <select value={r.preservation || 'fresh'} onChange={(ev) => onField(r.key, 'preservation', ev.target.value)}>
                        {PRESERVATION.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                </td>
                <td className="cl-td-sex">
                    <input type="checkbox" checked={!!r.sexed} onChange={(ev) => onSexed(r.key, ev.target.checked)} aria-label="Sexado" />
                </td>
            </>
        );
    };

    return (
        <div className="cl-table-wrap">
            <table className="cl-table">
                <thead>
                    <tr>
                        {!sameSemen && <th className="cl-th-semen">Semen</th>}
                        <th>Óv.</th>
                        {STAGES.map((s) => (
                            <th key={s.value} className="cl-th-stage" style={{ color: s.color }} title={s.label}>{s.short}</th>
                        ))}
                        <th>Emb.</th>
                        <th>Preserv.</th>
                        <th>Sex.</th>
                        <th aria-label="Acciones" />
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) => {
                        const primary = g.items[0];
                        const open = !collapsed.has(g.aspiration_id);
                        const posting = g.items.filter(willPost);
                        const totalEmb = posting.reduce((a, r) => a + embryosCreated(r.stages), 0);
                        const totalOoc = posting.reduce((a, r) => a + (Number(r.oocytes) || 0), 0);
                        const viables = primary.viables ?? 0;
                        const avail = primary.available ?? viables;
                        const over = totalEmb > viables || totalOoc > avail;
                        const pct = viables > 0 ? Math.min(100, Math.round((totalEmb / viables) * 100)) : (totalEmb > 0 ? 100 : 0);
                        return (
                            <Fragment key={g.aspiration_id}>
                                <tr className="cl-group">
                                    <td className="cl-group__cell" colSpan={colSpan}>
                                        <button type="button" className="cl-group__toggle" onClick={() => toggle(g.aspiration_id)} aria-expanded={open} aria-label={open ? 'Colapsar' : 'Desplegar'}>
                                            <Chevron open={open} />
                                        </button>
                                        <span className="cl-group__name">{primary.donorName}{primary.donorNumber ? ` · ${primary.donorNumber}` : ''}</span>
                                        <span className={`cl-group__meter ${over ? 'cl-group__meter--over' : ''}`} title={`${totalEmb}/${viables} embriones · óvocitos ${totalOoc}/${avail}`}>
                                            <span className="cl-donor-bar"><span className="cl-donor-bar__fill" style={{ width: `${pct}%` }} /></span>
                                            <span className="cl-donor-num">{totalEmb}/{viables} emb · óv {totalOoc}/{avail}</span>
                                        </span>
                                        {!open && g.items.length > 1 && <span className="cl-group__count">{g.items.length} clivage(s)</span>}
                                    </td>
                                </tr>
                                {open && g.items.map((r, j) => {
                                    const isLast = j === g.items.length - 1;
                                    return (
                                        <tr key={r.key} className="cl-tr cl-tr--clivage">
                                            {fields(r)}
                                            <td className="cl-td-actions">
                                                {j > 0 && (
                                                    <button type="button" className="cl-row__del" onClick={() => onRemove(r.key)} aria-label="Quitar clivage" title="Quitar este clivage">
                                                        <IconTrash />
                                                    </button>
                                                )}
                                                {isLast && (
                                                    <button type="button" className="cl-add-btn" onClick={() => onAddClivage(g.aspiration_id)} aria-label="Agregar clivage a esta donadora" title="Otro toro (otro clivage para esta donadora)">
                                                        <IconPlus />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
